import { useCallback, useEffect, useRef } from 'react';

interface ShowPromiseResult {
  done: boolean;
  description: string;
  state: 'load' | 'render' | 'playing' | 'destroy';
  error: boolean;
}

interface AdController {
  show: () => Promise<ShowPromiseResult>;
}

declare global {
  interface Window {
    Adsgram?: {
      init: (params: { blockId: string }) => AdController;
    };
  }
}

interface UseAdsgramParams {
  blockId: string;
  onReward: () => void;
  onError?: (result: ShowPromiseResult) => void;
}

export function useAdsgram({ blockId, onReward, onError }: UseAdsgramParams) {
  const adControllerRef = useRef<AdController | undefined>(undefined);

  useEffect(() => {
    adControllerRef.current = window.Adsgram?.init({ blockId });
  }, [blockId]);

  const showAd = useCallback(async () => {
    if (adControllerRef.current) {
      adControllerRef.current
        .show()
        .then(() => {
          onReward();
        })
        .catch((result: ShowPromiseResult) => {
          onError?.(result);
        });
    } else {
      onError?.({
        error: true,
        done: false,
        state: 'load',
        description: 'Adsgram script not loaded',
      });
    }
  }, [onReward, onError]);

  return showAd;
}
